import type { CrmEventDTO } from '../server/dto/crm-event-dto.ts';
import type { RecurrenceRule } from './rrule.ts';

export type EventMetadata = {
    is_recurring: boolean;
    has_set_time: boolean;
};

type EventTimingFields = {
    recurrenceRule?: RecurrenceRule;
    start_time?: string;
    end_time?: string;
};

/**
 * Extracts metadata about an event's recurrence and time settings
 * @param event The event to extract metadata from
 * @returns Object containing is_recurring and has_set_time flags
 */
export function getEventMetadata(event: EventTimingFields): EventMetadata {
    return {
        is_recurring: !!event.recurrenceRule,
        has_set_time: !!(event.start_time && event.end_time),
    };
}

export function sortEvents<T extends CrmEventDTO>(events: T[]): T[] {
    return events.toSorted((a, b) => {
        const aDate = a.date;
        const bDate = b.date;

        if (aDate !== bDate) {
            return aDate.localeCompare(bDate);
        }

        const aOrder = a.order[aDate] ?? Number.POSITIVE_INFINITY;
        const bOrder = b.order[bDate] ?? Number.POSITIVE_INFINITY;

        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }

        const dayEventA = a.start_time ? 0 : 1;
        const dayEventB = b.start_time ? 0 : 1;
        const startTimeA = a.start_time;
        const startTimeB = b.start_time;

        if (dayEventA !== dayEventB) {
            return dayEventA - dayEventB;
        }

        if (startTimeA && startTimeB) {
            return startTimeA.localeCompare(startTimeB);
        }

        return a.event_id.localeCompare(b.event_id);
    });
}
